import type { GameState, PlayerTally } from './replay';

/**
 * Player of the Game (gameplay.md 3.7). One formula for the whole app: the summary shows it
 * offline from the local fold, and stats stores the same pick in `player_game_lines.potg`
 * (stats imports these functions and does not re-implement them), so the name never changes when
 * the server's answer arrives.
 *
 *   GmSc = PTS + 0.7*REB + 0.7*AST + STL + 0.7*BLK - TO - 0.4*PF + (won ? 2 : 0)
 *
 * This is the simplified score on raw points. It is NOT the Hollinger `gmsc` that stats keeps for
 * full-depth leaderboards and records. Ties go to a player on the winning team, then fewer
 * turnovers, then more points, then the lower player id (so any device agrees).
 */

export function gameScore(
  t: Pick<PlayerTally, 'pts' | 'oreb' | 'dreb' | 'ast' | 'stl' | 'blk' | 'tov' | 'pf'>,
  won: boolean,
): number {
  const v = t.pts + 0.7 * (t.oreb + t.dreb) + 0.7 * t.ast + t.stl + 0.7 * t.blk - t.tov - 0.4 * t.pf + (won ? 2 : 0);
  return Math.round(v * 100) / 100;
}

export interface PlayerOfTheGame {
  playerId: string;
  score: number;
}

/**
 * The pick for a finished game, or null when there is none to make: the game is live or
 * abandoned, or nobody has a recorded individual stat (a final-score-only game).
 */
export function pickPlayerOfTheGame(state: GameState): PlayerOfTheGame | null {
  if (state.status !== 'final' && state.status !== 'over') return null;
  const rows = Object.values(state.players).map((t) => {
    const won = state.winner !== null && t.team === state.winner;
    const touched = t.pts + t.oreb + t.dreb + t.ast + t.stl + t.blk + t.tov + t.pf + t.fga + t.fta;
    return { t, won, touched, score: gameScore(t, won) };
  });
  if (rows.every((r) => r.touched === 0)) return null;
  rows.sort(
    (a, b) =>
      b.score - a.score ||
      Number(b.won) - Number(a.won) ||
      a.t.tov - b.t.tov ||
      b.t.pts - a.t.pts ||
      (a.t.playerId < b.t.playerId ? -1 : a.t.playerId > b.t.playerId ? 1 : 0),
  );
  const top = rows[0];
  return top ? { playerId: top.t.playerId, score: top.score } : null;
}
